import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box, Typography } from '@mui/material';

/**
 * `email` = die gewachsene Mailing-Konfiguration (Menüleiste, Tabellen, Bilder,
 * Platzhalter-Menü). `lp` = schlanke Leiste für die Fließtext-Felder des LP-Managers.
 *
 * Der Unterschied ist BEWUSST nur die Bedienoberfläche, nicht die erlaubten Elemente: beide
 * Presets fahren `valid_elements: '*[*]'`. Eine Tag-Allowlist im Editor wäre eine zweite,
 * abweichende Content-Policy neben `sanitizeContentHtml` (BrokerHubService) — und die verliert
 * schweigend, weil TinyMCE beim Laden normalisiert, ohne es zu melden. Gemessen 2026-08-19 über
 * alle lp_webinar_pages-Zeilen: eine „naheliegende" enge Liste (p/strong/em/a/ul/ol/li/br/span)
 * hätte h2, h3 und ein img in 3 von 6 Zeilen verworfen und alle <b> zu <strong> umgeschrieben.
 * Die Sicherheitsgrenze sitzt beim Persist, nicht hier.
 */
export type TinyMCEPreset = 'email' | 'lp';

interface TinyMCEEditorProps {
  value: string;
  onChange: (content: string) => void;
  /**
   * Feuert, wenn der Editor den Fokus bekommt. Gebraucht von Dialogen mit MEHREREN
   * Eingabefeldern, die wissen muessen, wohin ein Einfuegen-Knopf schreiben soll.
   * Ueber das TinyMCE-Event registriert, nicht ueber DOM-Bubbling: der Editor laeuft in
   * einem iframe, aus dem Fokus-Events nicht nach aussen blubbern.
   */
  onFocus?: () => void;
  height?: number;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /** Default 'email' — bestehende Aufrufer verhalten sich unverändert. */
  preset?: TinyMCEPreset;
  /**
   * Feuert genau einmal nach `init` mit dem Inhalt, den TinyMCE aus `value` gemacht hat.
   *
   * Wozu: der Editor normalisiert beim Laden (`<p>`-Umklammerung, Attributreihenfolge), und der
   * Wrapper fährt controlled — der normalisierte Stand landet also im Formularstate, ohne dass
   * jemand getippt hat. Wer den gespeichert bekommt, ändert den build_hash einer Seite, die
   * niemand angefasst hat.
   *
   * ⚠ Der naheliegende Riegel „melde echte Eingabe-Ereignisse" trägt NICHT. Gemessen 2026-08-19
   * beim blossen Öffnen des LP-Editors:
   *   BeforeSetContent → SetContent → LoadContent → init → NodeChange → ExecCommand(mceFocus) → …
   * TinyMCE feuert also von sich aus ein `ExecCommand`, und jede Positivliste, die `ExecCommand`
   * enthält (Toolbar-Klicks!), zählt das als Bearbeitung. Eine Blockliste einzelner Kommandos
   * müsste TinyMCEs internem Kommandosatz hinterherlaufen.
   *
   * Deshalb der INHALTS-Vergleich statt der Ereignis-Klassifikation: solange der aktuelle Wert
   * gleich dieser Grundlinie ist, hat niemand editiert — egal was gefeuert hat.
   */
  onNormalizedBaseline?: (html: string) => void;
}

export interface TinyMCEEditorRef {
  insertContent: (content: string) => void;
  setContent: (content: string) => void;
  getContent: () => string;
  focus: () => void;
}

/**
 * TinyMCE HTML Editor Component
 * Used for editing email HTML content in webinar mailings
 * Exposes insertContent method via ref for external placeholder insertion
 */
const TinyMCEEditor = forwardRef<TinyMCEEditorRef, TinyMCEEditorProps>(
  (
    {
      value,
      onChange,
      onFocus,
      height = 400,
      placeholder = 'Enter HTML content...',
      label,
      disabled = false,
      preset = 'email',
      onNormalizedBaseline,
    },
    ref,
  ) => {
    const editorRef = useRef<any>(null);
    // Über eine Ref gelesen, damit ein wechselnder Callback das `init`-Objekt nicht neu erzeugt
    // (das würde den Editor remounten und den Cursor verlieren).
    const onBaselineRef = useRef(onNormalizedBaseline);
    onBaselineRef.current = onNormalizedBaseline;

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      insertContent: (content: string) => {
        if (editorRef.current) {
          editorRef.current.insertContent(content);
        }
      },
      setContent: (content: string) => {
        if (editorRef.current) {
          // Use raw format to preserve all HTML/styles without sanitization
          editorRef.current.setContent(content, { format: 'raw' });
        }
      },
      getContent: () => {
        if (editorRef.current) {
          return editorRef.current.getContent();
        }
        return '';
      },
      focus: () => {
        if (editorRef.current) {
          editorRef.current.focus();
        }
      },
    }));

    const handleEditorChange = (content: string) => {
      onChange(content);
    };

    /**
     * Grundlinie einmalig melden. Zum Zeitpunkt von `init` steht der Inhalt bereits (die
     * gemessene Reihenfolge ist SetContent → LoadContent → init), `getContent()` liefert dort
     * also genau die normalisierte Fassung des Serverwerts.
     */
    const bindBaseline = (editor: any): void => {
      editor.on('init', () => {
        onBaselineRef.current?.(editor.getContent());
      });
    };

    // Schlanke Leiste für die Fließtext-Felder des LP-Managers. Kein Platzhalter-Menü (das ist
    // Mailing-Sache), kein Base64-Bildupload (Bilder laufen über ImageUploadField +
    // /api/webinar-lp/upload-image). `blocks` bleibt drin, weil der Bestand h2/h3 benutzt.
    const lpInit = {
      height,
      menubar: false,
      plugins: ['autolink', 'lists', 'link', 'code', 'wordcount'],
      toolbar:
        'undo redo | blocks | bold italic | forecolor | bullist numlist | link | removeformat | code',
      block_formats: 'Absatz=p; Überschrift 2=h2; Überschrift 3=h3',
      content_style: `
            body {
              font-family: 'Sofia Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
              font-size: 15px;
              margin: 10px;
            }
          `,
      placeholder,
      branding: false,
      promotion: false,
      valid_elements: '*[*]',
      extended_valid_elements: '*[*]',
      verify_html: false,
      setup: bindBaseline,
    };

    return (
      <Box>
        {label && (
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            {label}
          </Typography>
        )}
        <Editor
          // Selbst gehostet aus react-app/public/tinymce (v8.2.2, liegt seit jeher im Repo).
          // Damit entfällt REACT_APP_TINYMCE_API_KEY, der Request an cdn.tiny.cloud und die
          // Domain-Bindung des Cloud-Keys.
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          // TinyMCE 7+ VERLANGT einen Lizenzschlüssel und DEAKTIVIERT den Editor sonst — sichtbar
          // als Hinweisleiste „The editor is disabled because a TinyMCE license key has not been
          // provided." (gemessen 2026-08-19 beim Umstieg; über den Cloud-Key fiel das nie auf).
          // public/tinymce ist die GPLv2+-Distribution (license.md), deren Build 'gpl' akzeptiert.
          // ⚠ Als PROP, nicht in `init`: der React-Wrapper überschreibt `init.license_key` und
          // markiert es als DoNotUse — tsc weist es ab.
          licenseKey="gpl"
          onInit={(evt, editor) => {
            editorRef.current = editor;
            if (onFocus) editor.on('focus', onFocus);
          }}
          value={value}
          onEditorChange={handleEditorChange}
          disabled={disabled}
          init={preset === 'lp' ? lpInit : {
            height: height,
            menubar: true,
            plugins: [
              'advlist',
              'autolink',
              'lists',
              'link',
              'image',
              'charmap',
              'preview',
              'anchor',
              'searchreplace',
              'visualblocks',
              'code',
              'fullscreen',
              'insertdatetime',
              'media',
              'table',
              'help',
              'wordcount',
            ],
            toolbar:
              'undo redo | blocks | ' +
              'bold italic forecolor | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | ' +
              'link image | code | removeformat | help',
            content_style: `
            body {
              font-family: Helvetica, Arial, sans-serif;
              font-size: 14px;
              margin: 10px;
            }
          `,
            placeholder: placeholder,
            branding: false,
            promotion: false,
            // Security: Allow all content for email templates
            valid_elements: '*[*]',
            extended_valid_elements: '*[*]',
            // Preserve styles (extended for email template imports)
            valid_styles: {
              '*': 'color,background-color,font-size,font-family,font-weight,text-decoration,text-align,border,border-radius,border-collapse,padding,padding-top,padding-right,padding-bottom,padding-left,margin,margin-top,margin-right,margin-bottom,margin-left,width,height,max-width,min-width,display,background,line-height,float,vertical-align,white-space,position,top,right,bottom,left,clear,overflow,-webkit-border-radius,-moz-border-radius,-webkit-text-size-adjust',
            },
            // Allow inline styles
            verify_html: false,
            // Image upload settings (local upload for now)
            images_upload_handler: (blobInfo: any) => {
              return new Promise((resolve, reject) => {
                // Convert to base64 for simplicity
                const reader = new FileReader();
                reader.onloadend = () => {
                  resolve(reader.result as string);
                };
                reader.onerror = () => {
                  reject('Error reading file');
                };
                reader.readAsDataURL(blobInfo.blob());
              });
            },
            // Custom toolbar for email-specific features
            setup: (editor: any) => {
              bindBaseline(editor);
              // Add custom placeholder button
              editor.ui.registry.addMenuButton('placeholders', {
                text: 'Platzhalter',
                fetch: (callback: any) => {
                  const items = [
                    // Core placeholders
                    {
                      type: 'menuitem',
                      text: 'Anmelde-Link',
                      onAction: () => editor.insertContent('{WEBINAR_LINK}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Webinar-Titel (öffentlich)',
                      onAction: () => editor.insertContent('{WEBINAR_TITLE}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'E-Mail',
                      onAction: () => editor.insertContent('{EMAIL}'),
                    },
                    // Separator
                    { type: 'separator' },
                    // Dynamic date placeholders
                    {
                      type: 'menuitem',
                      text: 'Datum dynamisch (Morgen um...)',
                      onAction: () => editor.insertContent('{WEBINAR_DATE_DYNAMIC}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Datum dynamisch GROSS',
                      onAction: () => editor.insertContent('{WEBINAR_DATE_DYNAMIC_UPPER}'),
                    },
                    // Separator
                    { type: 'separator' },
                    // Static date placeholders
                    {
                      type: 'menuitem',
                      text: 'Wochentag (Samstag)',
                      onAction: () => editor.insertContent('{WEBINAR_DAY_WRITTEN}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Tag (01.)',
                      onAction: () => editor.insertContent('{WEBINAR_DATE:d.}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Tag.Monat (01.01.)',
                      onAction: () => editor.insertContent('{WEBINAR_DATE:d.m.}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Volles Datum (01.01.2025)',
                      onAction: () => editor.insertContent('{WEBINAR_DATE:d.m.Y}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Jahr (2025)',
                      onAction: () => editor.insertContent('{WEBINAR_DATE:Y}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Monat (März)',
                      onAction: () => editor.insertContent('{WEBINAR_MONAT}'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Uhrzeit (11:00)',
                      onAction: () => editor.insertContent('{WEBINAR_TIME}'),
                    },
                  ];
                  callback(items);
                },
              });
            },
          }
          }
        />
      </Box>
    );
  },
);

TinyMCEEditor.displayName = 'TinyMCEEditor';

export default TinyMCEEditor;
